import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api, { formatApiError } from "@/lib/api";

const COLORS = ["#3B594C", "#C87D65", "#E0A96D", "#527867", "#88988E", "#B36B54", "#22312A", "#A0B5AB"];

function fmtDate(iso) {
  const d = new Date(iso);
  return `${d.getDate()}/${d.getMonth() + 1}`;
}

export default function SymptomPanel() {
  const [range, setRange] = useState(30);
  const [symptoms, setSymptoms] = useState([]);
  const [series, setSeries] = useState([]);
  const [today, setToday] = useState({});
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const load = (r) => {
    api.get(`/symptoms?range=${r}`).then((res) => {
      setSymptoms(res.data.symptoms);
      setSeries(res.data.series);
      setToday(res.data.today || {});
      setNotes(res.data.today_notes || "");
    });
  };

  useEffect(() => { load(range); }, [range]);

  const setSeverity = (s, v) => setToday((t) => ({ ...t, [s]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const entries = Object.entries(today)
        .filter(([, v]) => v != null)
        .map(([symptom, severity]) => ({ symptom, severity: Number(severity) }));
      await api.post("/symptoms", { entries, notes });
      toast.success("Symptoms logged for today");
      load(range);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const chartData = series.map((row) => ({ ...row, date: fmtDate(row.date + "T00:00:00") }));
  const activeSymptoms = symptoms.filter((s) => chartData.some((d) => d[s] != null));

  return (
    <div data-testid="symptom-panel">
      <div className="bg-app-secondary border border-subtle rounded-2xl p-4 mt-2">
        <p className="text-xs text-ink-muted leading-relaxed">
          This is a personal self tracking tool to help you notice patterns. It is not a diagnosis or medical assessment.
        </p>
      </div>

      {/* today's logger */}
      <div className="mt-4">
        <p className="overline-label">Log today (0 to 10)</p>
        <div className="mt-3 space-y-3">
          {symptoms.map((s) => {
            const val = today[s] ?? 0;
            return (
              <div key={s} className="bg-white rounded-2xl p-4 card-shadow">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-ink">{s}</span>
                  <span className="font-serif text-lg font-bold text-brand">{val}</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={10}
                  value={val}
                  onChange={(e) => setSeverity(s, Number(e.target.value))}
                  className="reset-slider"
                  data-testid={`symptom-slider-${s.replace(/[^a-z]/gi, "").toLowerCase()}`}
                />
              </div>
            );
          })}
        </div>
        <textarea
          data-testid="symptom-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className="mt-3 w-full rounded-2xl border border-subtle bg-white px-4 py-3 text-ink outline-none focus:border-[var(--brand)] resize-none"
          placeholder="Optional notes about today…"
        />
        <button
          data-testid="symptom-save"
          onClick={save}
          disabled={busy}
          className="mt-3 w-full bg-brand hover:bg-[var(--brand-hover)] text-white font-semibold py-3.5 rounded-full transition-colors disabled:opacity-60"
        >
          {busy ? "Saving…" : "Save Today's Symptoms"}
        </button>
      </div>

      {/* trends */}
      <div className="mt-6 flex items-center gap-2 bg-app-secondary rounded-full p-1 w-fit">
        {[7, 14, 30].map((r) => (
          <button
            key={r}
            data-testid={`symptom-range-${r}`}
            onClick={() => setRange(r)}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${range === r ? "bg-white text-brand card-shadow" : "text-ink-muted"}`}
          >
            {r} days
          </button>
        ))}
      </div>

      <div className="mt-4 bg-white rounded-2xl p-5 card-shadow" data-testid="symptom-chart">
        <div className="font-semibold text-ink mb-3">Symptom trends</div>
        {chartData.length === 0 ? (
          <div className="h-[180px] flex items-center justify-center text-ink-muted text-sm text-center px-4">
            No symptom logs yet. Track a few days to see your patterns here.
          </div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={chartData} margin={{ top: 5, right: 5, left: -25, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5DFD5" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#88988E" }} tickLine={false} axisLine={false} />
                <YAxis domain={[0, 10]} tick={{ fontSize: 11, fill: "#88988E" }} tickLine={false} axisLine={false} />
                <Tooltip />
                {activeSymptoms.map((s, i) => (
                  <Line key={s} type="monotone" dataKey={s} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={false} connectNulls />
                ))}
              </LineChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3">
              {activeSymptoms.map((s, i) => (
                <div key={s} className="flex items-center gap-1.5 text-xs text-ink-soft">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} /> {s}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
